"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCheckOutline18 } from "nucleo-ui-outline-18";
import type { ParamDefinition } from "@/hooks/useX402Endpoints";
import type { X402ImportStep } from "./X402ImportWizardUtils";

// Helper component to render discovered params
export function ParamList(t0) {
  const $ = _c(14);
  const {
    title,
    params,
    color: t1
  } = t0;
  const color = t1 === undefined ? "zinc" : t1;
  if (!params || Object.keys(params).length === 0) {
    return null;
  }
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800",
    green: "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800",
    purple: "bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-800",
    zinc: "bg-zinc-50 border-ds-border-default dark:bg-ds-neutral-800/50"
  };
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      blue: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400",
      green: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400",
      purple: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-400",
      zinc: "bg-ds-neutral-200 text-zinc-800 dark:bg-ds-neutral-700 dark:text-zinc-300"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const badgeColors = t2;
  const t3 = `rounded-lg border p-3 ${colorClasses[color]}`;
  let t4;
  if ($[1] !== title) {
    t4 = <h5 className="text-ds-text-secondary mb-2 text-xs font-medium">{title}</h5>;
    $[1] = title;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== color || $[4] !== params) {
    let t6;
    if ($[6] !== color) {
      t6 = t7 => {
        const [name, param] = t7;
        return <div key={name} className="flex flex-wrap items-center gap-1.5 text-xs"><code className="bg-ds-neutral-200 dark:bg-ds-neutral-700 rounded px-1.5 py-0.5 font-mono text-zinc-800 dark:text-zinc-200">{name}</code><span className={`rounded px-1.5 py-0.5 ${badgeColors[color]}`}>{param.type}</span>{param.required && <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-700 dark:bg-red-900/40 dark:text-red-400">required</span>}{param.default !== undefined && <span className="text-ds-text-muted">default: {JSON.stringify(param.default)}</span>}{param.description && <span className="text-ds-text-muted">— {param.description}</span>}</div>;
      };
      $[6] = color;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    t5 = Object.entries(params).map(t6);
    $[3] = color;
    $[4] = params;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <div className="space-y-1.5">{t5}</div>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t6) {
    t7 = <div className={t3}>{t4}{t6}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}

// Step indicator component for reuse
export function X402StepIndicator(t0) {
  const $ = _c(8);
  const {
    currentStep,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "page" : t1;
  const isModal = variant === "modal";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["input", "probe", "success"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const displaySteps = t2 as const;
  let t3;
  if ($[1] !== isModal) {
    t3 = isModal ? undefined : ["URL", "Payment", "Done"];
    $[1] = isModal;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const stepLabels = t3;
  const currentStepIndex = currentStep === "importing" ? 2 : displaySteps.indexOf(currentStep as (typeof displaySteps)[number]);
  let t4;
  if ($[3] !== currentStep || $[4] !== currentStepIndex || $[5] !== isModal || $[6] !== stepLabels) {
    t4 = <div className="flex items-center justify-center gap-2">{displaySteps.map((s, i) => {
        const isActive = currentStep === s || currentStep === "importing" && s === "success";
        const isCompleted = currentStepIndex > i;
        return <div key={s} className="flex items-center gap-2"><div className={`flex flex-col items-center ${stepLabels ? "gap-1" : ""}`}><div className={`flex items-center justify-center rounded-full font-medium ${isModal ? "h-6 w-6 text-xs" : "h-8 w-8 text-sm"} ${isActive ? "bg-blue-500 text-white" : isCompleted ? "bg-green-500 text-white" : "bg-ds-neutral-200 text-ds-text-subtle dark:bg-ds-neutral-700 dark:text-ds-text-disabled"}`}>{isCompleted && !isActive ? <IconCheckOutline18 className={isModal ? "h-3 w-3" : "h-4 w-4"} /> : i + 1}</div>{stepLabels && <span className="label-xs">{stepLabels[i]}</span>}</div>{i < 2 && <div className={`${stepLabels ? "mb-5" : ""} h-0.5 ${isModal ? "w-6" : "w-12"} ${isCompleted && !isActive ? "bg-green-500" : "bg-ds-neutral-200 dark:bg-ds-neutral-700"}`} />}</div>;
      })}</div>;
    $[3] = currentStep;
    $[4] = currentStepIndex;
    $[5] = isModal;
    $[6] = stepLabels;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}